import Head from 'next/head';
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';
import { basicPageProps, fetchTemplate, mobileProps } from '@/utils/isServer';
import CardNews from '@/components/CardNews';
import { cacheFetch } from '@/helpers/cacheFetch';
import { useRouter } from 'next/router';
import { useMobxStores } from '@/stores';
import { useEffect, useState } from 'react';
import { AutoComplete, Button, Input } from 'antd';
import SearchStore from '@/stores/SearchStore';
import { modalType } from '@/components/Modal';
import InputEmail from '@/components/InputEmail';

const ContentHeader = dynamic(() => import('@components/ContentHeader'), {
  loading: () => <p>Loading</p>,
});

function extractPrefixSuffix(url) {
  let supressFromHome = false;
  let suffix = '';
  let prefix = '/pressroom/';
  if (url.indexOf('?') != -1) {
    suffix = url.substring(url.indexOf('?'));
  }
  if (url.indexOf('/myapp') != -1) {
    prefix = '/myapp/pressroom/';
  }
  if (suffix == '?fromhome=true') {
    suffix = '';
    supressFromHome = true;
  }
  return [prefix, suffix, supressFromHome];
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  params,
  query,
}) => {
  const { isMobile } = mobileProps({ req });
  const { baseUrl, configInfo } = await basicPageProps({ req });
  const presses = await cacheFetch(`${baseUrl}/api/Presses`);
  return {
    props: {
      presses,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
    },
  };
};

function Press({ presses, configInfo }) {
  const {
    searchStore,
  }: {
    searchStore: SearchStore;
  } = useMobxStores();

  const [selectedDomain, setSelectedDomain] = useState('');
  const [email, setEmail] = useState<string>('');
  const handleSelect = (value) => {
    if (value.includes('@')) {
      setSelectedDomain(value);
      if (setEmail) {
        setEmail(value);
      }
    } else {
      setSelectedDomain(selectedDomain + value);
      if (setEmail) {
        setEmail(selectedDomain + value); // Append selected domain if missing
      }
    }
  };

  const doSubscribe = async () => {
    if (email) {
      await searchStore.subscribeRegular(1, email, '');
      modalType({
        type: 'success',
        title: 'You`re all set!',
        okButtonProps: {
          className: 'bg-primary',
        },
        content: (
          <p>
            Keep an eye on your inbox for exclusive deals, travel tips, and the
            latest parking updates. Thanks for subscribing—see you soon!
          </p>
        ),
        onOk: () => {
          setEmail('');
        },
      });
      setEmail('');
    }
  };

  return (
    <>
      <Head>
        <title>Newsroom | On Air Parking</title>
        <meta
          name="description"
          content="We exclusively choose only the best airport parking providers, shuttle service to and from the airport and a guaranteed parking space. Our Parking Reservation Specialist is at your service from 10am-7pm  ET."
        />
      </Head>
      <ContentHeader btitle="Newsroom" />
      <div className="container px-4 mx-auto">
        <div className="flex flex-col pt-8 text-base md:text-xl my-12">
          <h2 className="text-2xl md:text-3xl">Contact Us</h2>
          <p className="mt-10 md:mt-6">
            Our team is here to provide the latest news, expert travel tips, and
            valuable insights to keep travelers informed and inspired.
          </p>
          <p className="mt-10 md:mt-6">
            For media inquiries, please use our{' '}
            <a className="text-secondary" href="/contact">
              contact form
            </a>
            .
          </p>
        </div>

        <div className="flex flex-col lg:flex-row justify-center items-center gap-10 md:gap-20 my-12 mb-12">
          <div className="border border-primary/50 p-6 text-center flex flex-col items-center justify-center w-48 h-48 md:w-64 md:h-28 rounded-md">
            <p className="text-2xl md:text-3xl font-bold">2016</p>
            <p className="text-base md:text-xl mt-2">Year Founded</p>
          </div>
          <div className="border border-primary/50 p-6 text-center flex flex-col items-center justify-center w-48 h-48 md:w-64 md:h-28 rounded-md">
            <p className="text-2xl md:text-3xl font-bold">800.000+</p>
            <p className="text-base md:text-xl mt-2">Reservations Booked</p>
          </div>
          <div className="border border-primary/50 p-6 text-center flex flex-col items-center justify-center w-48 h-48 md:w-64 md:h-28 rounded-md">
            <p className="text-2xl md:text-3xl font-bold">40.000</p>
            <p className="text-base md:text-xl mt-2">Customer Reviews</p>
          </div>
          <div className="border border-primary/50 p-6 text-center flex flex-col items-center justify-center w-48 h-48 md:w-64 md:h-28 rounded-md">
            <p className="text-2xl md:text-3xl font-bold">300</p>
            <p className="text-base md:text-xl mt-2">Parking Partners</p>
          </div>
        </div>
        <div className="flex flex-col p-8 text-base md:text-xl my-12 bg-gray-50 ">
          <h2 className="text-2xl md:text-3xl">Subscribe</h2>
          <p className="my-4">
            Subscribe to our newsletter for exclusive deals, travel tips, and
            the latest updates on hassle-free parking solutions!
          </p>
          <div className="flex flex-row items-center justify-center gap-4 md:w-1/3 self-center">
            <label className="sr-only" htmlFor="email-input">
              Subscribe to our newsletter
            </label>
            <InputEmail
              email={email}
              inputId="email-input"
              handleSelect={handleSelect}
              setEmail={setEmail}
            />
            <Button type="primary" onClick={doSubscribe}>
              SUBSCRIBE
            </Button>
          </div>
        </div>

        <div className="text-base md:text-xl flex flex-col ">
          <p className="text-2xl md:text-3xl">News</p>
          <p className="mt-10 md:mt-6">
            Our media relations team is committed to collaborating with the
            press to deliver valuable information for travelers everywhere.
          </p>

          <CardNews news={presses.result} configInfo={configInfo} />
        </div>
      </div>
    </>
  );
}

export default Press;
